import { useEffect, useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@features/auth';
import { api } from '@shared/api';
import { env } from '@shared/config/env';
import { DEFAULT_LOCALE, isValidLocale, syncLanguageFromUrl } from '@shared/config/i18n';
import { Button, Spinner, Text } from '@shared/ui';

const searchSchema = z.object({
  requestId: z.uuid(),
  installationOrigin: z.url(),
});

function wasAborted(controller: AbortController): boolean {
  return controller.signal.aborted;
}

export const Route = createFileRoute('/$locale_/helpdesk_/connect')({
  validateSearch: searchSchema,
  beforeLoad: ({ params }) => {
    if (!isValidLocale(params.locale)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirects are thrown.
      throw redirect({ to: '/$locale/helpdesk', params: { locale: DEFAULT_LOCALE } });
    }
    syncLanguageFromUrl(params.locale);
  },
  component: HelpdeskConnect,
});

function HelpdeskConnect() {
  const { locale } = Route.useParams();
  const { requestId, installationOrigin } = Route.useSearch();
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const [state, setState] = useState<'waiting' | 'connecting' | 'done' | 'error'>('waiting');
  useEffect(() => {
    if (isLoading) return;
    const origin = new URL(installationOrigin);
    const opener = window.opener as Window | null;
    if (origin.protocol !== 'https:' || origin.origin !== installationOrigin ||
        !opener || opener === window) {
      setState('error');
      return;
    }
    if (!user) {
      const returnTo = window.location.pathname + window.location.search;
      window.location.replace(`/${locale}/login?redirect=${encodeURIComponent(returnTo)}`);
      return;
    }
    const lifecycle = { handled: false };
    const controller = new AbortController();
    const announce = () => {
      opener.postMessage({ type: 'move-helpdesk-ready', requestId }, origin.origin);
    };
    const handleMessage = async (event: MessageEvent<unknown>) => {
      if (event.origin !== origin.origin || event.source !== opener || controller.signal.aborted || lifecycle.handled) return;
      const input = z.object({
        type: z.literal('move-helpdesk-connect'), requestId: z.uuid(), installationOrigin: z.url(),
      }).strict().safeParse(event.data);
      if (!input.success || input.data.requestId !== requestId ||
          input.data.installationOrigin !== origin.origin) return;
      lifecycle.handled = true;
      window.clearInterval(interval);
      setState('connecting');
      try {
        const issue = () => fetch(new URL('/api/v1/helpdesk/sessions', env.VITE_API_URL), {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ installationOrigin: origin.origin }),
          signal: controller.signal,
        });
        let response = await issue();
        if (response.status === 401) {
          await api.refreshSession();
          response = await issue();
        }
        if (!response.ok) throw new Error('Helpdesk connection failed');
        if (wasAborted(controller)) return;
        opener.postMessage({
          type: 'move-helpdesk-connected', requestId, installationOrigin: origin.origin,
          session: await response.json() as unknown,
        }, origin.origin);
        setState('done');
      } catch {
        if (!wasAborted(controller)) {
          opener.postMessage({ type: 'move-helpdesk-error', requestId }, origin.origin);
          setState('error');
        }
      }
    };
    const onMessage = (event: MessageEvent<unknown>) => { void handleMessage(event); };
    window.addEventListener('message', onMessage);
    const interval = window.setInterval(announce, 1000);
    announce();
    return () => {
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener('message', onMessage);
    };
  }, [installationOrigin, isLoading, locale, requestId, user]);
  return <main className="mx-auto flex min-h-dvh max-w-lg flex-col items-center justify-center gap-4 p-6 text-center">
    <h1 className="text-2xl font-semibold">{t('helpdesk.widget.connectTitle', 'Connect Move Helpdesk')}</h1>
    {isLoading || state === 'waiting' || state === 'connecting' ?
      <Spinner aria-label={t('common.loading', 'Loading...')} /> : null}
    <Text role="status">{state === 'done'
      ? t('helpdesk.widget.connectDone', 'Connected. You can return to the Helpdesk widget.')
      : state === 'error'
        ? t('helpdesk.widget.connectFailed', 'Connection failed. Return to the widget and try again.')
        : t('helpdesk.widget.connectWaiting', 'Connecting your Move account…')}</Text>
    {state === 'done' ? <Button onClick={() => { window.close(); }}>{t('common.close', 'Close')}</Button> : null}
  </main>;
}
